/**
 * Layout editor — /settings/extensions/$moduleKey/$entityKey/layout
 *
 * Two modes via tabs:
 *   - Visual: tree-based node editor (LayoutEditor component) with an
 *     inspector for the selected node
 *   - Source: raw JSON textarea for power users
 *
 * Both modes write to the same `LayoutManifest` draft state. Save sends
 * the draft to `extensions.layouts.save`; the backend runs the full
 * structural validator and returns errors if core fields are missing.
 */

import type { LayoutManifest } from '@cloud/modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft, RotateCcw, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { LayoutEditor } from '@/components/custom/layout-editor'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useUrlTab } from '@/hooks/use-url-tab'
import { errorMessage } from '@/lib/error-message'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/extensions/$moduleKey/$entityKey/layout')({
  component: LayoutEditorPage,
})

function LayoutEditorPage() {
  const { moduleKey, entityKey } = Route.useParams()
  const queryClient = useQueryClient()
  const [tab, setTab] = useUrlTab('visual')

  const { data: layoutData, isLoading } = useQuery(
    orpc.extensions.layouts.get.queryOptions({
      input: { moduleKey, entityKey, kind: 'detail' },
    }),
  )

  // Pull the entity itself so we know which fields exist for the
  // visual editor's pickers (Field / Computed / Scenario grid nodes).
  const { data: customEntity } = useQuery(
    orpc.modules.custom.entities.get.queryOptions({
      input: { moduleKey, key: entityKey },
    }),
  )

  const t = useTranslations()
  const [draft, setDraft] = useState<LayoutManifest | null>(null)
  const [sourceText, setSourceText] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)

  // Hydrate draft from the server on first load.
  useEffect(() => {
    if (!layoutData) return
    const parsed = layoutData.layout as unknown as LayoutManifest
    setDraft(parsed)
    setSourceText(JSON.stringify(parsed, null, 2))
    setParseError(null)
  }, [layoutData])

  const saveMutation = useMutation(
    orpc.extensions.layouts.save.mutationOptions({
      onSuccess: () => {
        toast.success('Layout saved')
        void queryClient.invalidateQueries({
          queryKey: orpc.extensions.layouts.get.queryKey({
            input: { moduleKey, entityKey, kind: 'detail' },
          }),
        })
      },
      onError: (err) => toast.error(errorMessage(err, t)),
    }),
  )

  const resetMutation = useMutation(
    orpc.extensions.layouts.reset.mutationOptions({
      onSuccess: () => {
        toast.success('Layout reset to default')
        void queryClient.invalidateQueries({
          queryKey: orpc.extensions.layouts.get.queryKey({
            input: { moduleKey, entityKey, kind: 'detail' },
          }),
        })
      },
      onError: (err) => toast.error(errorMessage(err, t)),
    }),
  )

  const handleSave = () => {
    if (!draft) return
    saveMutation.mutate({
      moduleKey,
      entityKey,
      kind: 'detail',
      layout: draft as unknown as { version: 2; root: Record<string, unknown> },
    })
  }

  // Bevestigen via AlertDialog in plaats van een native `confirm()`; de tekst was
  // bovendien hardgecodeerd Engels en staat nu in alle vier de woordenboeken.
  const [confirmReset, setConfirmReset] = useState(false)
  const handleReset = () => setConfirmReset(true)

  // Sync the JSON source text whenever the visual editor mutates the
  // draft, so switching to Source mode shows the current state.
  const handleVisualChange = (next: LayoutManifest) => {
    setDraft(next)
    setSourceText(JSON.stringify(next, null, 2))
  }

  // Parse the JSON source on every keystroke so toggling back to
  // Visual mode picks up the latest valid tree. Bad JSON keeps the
  // previous draft and surfaces the parser error inline.
  const handleSourceChange = (text: string) => {
    setSourceText(text)
    try {
      const parsed = JSON.parse(text) as LayoutManifest
      setDraft(parsed)
      setParseError(null)
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Invalid JSON')
    }
  }

  if (isLoading || !layoutData || !draft) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-6xl">
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  // Field metadata for the visual editor's pickers — combine custom
  // entity fields (when this is a custom entity) with the layout
  // payload's coreFields (when it's a built-in entity).
  const availableFields: Array<{ key: string; label: string }> = customEntity
    ? (customEntity.fields as Array<{ key: string; label?: string }>).map((f) => ({
        key: f.key,
        label: f.label ?? f.key,
      }))
    : layoutData.coreFields.map((f) => ({ key: f.key, label: f.label }))

  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl space-y-4">
      <Link
        to="/settings"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to settings
      </Link>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>
              Layout for{' '}
              <code className="font-mono text-base">
                {moduleKey}.{entityKey}
              </code>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {layoutData.source === 'override' && 'Custom layout (override active)'}
              {layoutData.source === 'default' && 'Using module default layout'}
              {layoutData.source === 'fallback' && 'Using auto-generated fallback layout'}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {layoutData.source === 'override' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={resetMutation.isPending}
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset
              </Button>
            )}
            <Button type="button" onClick={handleSave} disabled={saveMutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {saveMutation.isPending ? 'Saving…' : 'Save layout'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="visual">Visual</TabsTrigger>
              <TabsTrigger value="source">Source</TabsTrigger>
            </TabsList>
            <TabsContent value="visual" className="mt-4">
              <LayoutEditor
                value={draft}
                onChange={handleVisualChange}
                availableFields={availableFields}
              />
            </TabsContent>
            <TabsContent value="source" className="mt-4 space-y-2">
              <Label className="text-xs">Layout JSON</Label>
              <Textarea
                value={sourceText}
                onChange={(e) => handleSourceChange(e.target.value)}
                rows={28}
                className="font-mono text-xs"
                spellCheck={false}
              />
              {parseError && <p className="text-xs text-destructive">{parseError}</p>}
              <p className="text-xs text-muted-foreground">
                The backend runs full validation on save — core fields can be hidden but not
                removed.
              </p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.resetLayout')}</AlertDialogTitle>
            <AlertDialogDescription>{t('common.resetLayoutConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                resetMutation.mutate({ moduleKey, entityKey, kind: 'detail' })
                setConfirmReset(false)
              }}
            >
              {t('common.resetLayout')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
